import { recordTimelinePlaybackPerf } from './perfHooks'

export type PlaybackBackgroundWorkAction = 'deferred' | 'skipped' | 'resumed' | 'playback-critical'

export type PlaybackBackgroundWorkDetails = {
  reason?: string
  count?: number
}

type PlaybackActivityListener = (active: boolean) => void

type DeferredBackgroundWork = {
  key: string
  kind: string
  run: () => void
  queuedAt: number
}

const RESUME_DEBOUNCE_MS = 500
const RESUME_BATCH_SIZE = 4
const RESUME_IDLE_TIMEOUT_MS = 1500

let timelinePlaybackActive = false
let resumeTimer: ReturnType<typeof setTimeout> | null = null
let resumeIdleCallback: number | null = null

const listeners = new Set<PlaybackActivityListener>()
const deferredWork = new Map<string, DeferredBackgroundWork>()
const actionCounts: Record<string, number> = {}
const kindActionCounts: Record<string, Record<string, number>> = {}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

function isBrowser(): boolean {
  return typeof window !== 'undefined'
}

function incrementCount(kind: string, action: PlaybackBackgroundWorkAction, count: number): void {
  actionCounts[action] = (actionCounts[action] ?? 0) + count
  if (!kindActionCounts[kind]) kindActionCounts[kind] = {}
  kindActionCounts[kind][action] = (kindActionCounts[kind][action] ?? 0) + count
}

export function recordPlaybackBackgroundWork(
  kind: string,
  action: PlaybackBackgroundWorkAction,
  details: PlaybackBackgroundWorkDetails = {},
): void {
  const count = details.count ?? 1
  if (count <= 0) return
  incrementCount(kind, action, count)
  recordTimelinePlaybackPerf({
    type: 'background-work',
    kind,
    action,
    count,
    reason: details.reason,
  })
}

function cancelDeferredFlush(): void {
  if (resumeTimer !== null) {
    clearTimeout(resumeTimer)
    resumeTimer = null
  }
  if (resumeIdleCallback !== null && isBrowser() && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(resumeIdleCallback)
    resumeIdleCallback = null
  }
}

function scheduleIdleDrain(): void {
  if (timelinePlaybackActive || deferredWork.size === 0 || resumeIdleCallback !== null) return

  const drain = (deadline?: IdleDeadline) => {
    resumeIdleCallback = null
    if (timelinePlaybackActive) return

    let processed = 0
    while (deferredWork.size > 0 && processed < RESUME_BATCH_SIZE) {
      if (deadline && !deadline.didTimeout && deadline.timeRemaining() < 4) break

      const next = deferredWork.values().next().value
      if (!next) break
      deferredWork.delete(next.key)
      processed++
      recordPlaybackBackgroundWork(next.kind, 'resumed')

      try {
        next.run()
      } catch (error) {
        setTimeout(() => {
          throw error
        }, 0)
      }
    }

    if (deferredWork.size > 0) {
      scheduleDeferredBackgroundWorkFlush(0)
    }
  }

  if (isBrowser() && 'requestIdleCallback' in window) {
    resumeIdleCallback = window.requestIdleCallback(drain, { timeout: RESUME_IDLE_TIMEOUT_MS })
    return
  }

  resumeTimer = setTimeout(() => {
    resumeTimer = null
    drain()
  }, 0)
}

function scheduleDeferredBackgroundWorkFlush(delayMs = RESUME_DEBOUNCE_MS): void {
  if (timelinePlaybackActive || deferredWork.size === 0 || resumeTimer !== null || resumeIdleCallback !== null) return

  resumeTimer = setTimeout(() => {
    resumeTimer = null
    scheduleIdleDrain()
  }, delayMs)
}

export function isTimelinePlaybackActive(): boolean {
  return timelinePlaybackActive
}

// Latches once the program monitor has decoded its first frame for a project. Decorative
// media work (trimmed-clip thumbnail seeks) waits for it so it never competes with the
// frame the user is waiting on. Keyed by project: an in-app switch to another project
// starts over.
let firstFrameReadyProjectId: string | null = null
const firstFrameListeners = new Set<() => void>()

export function isTimelineFirstFrameReady(projectId: string | null): boolean {
  return projectId !== null && firstFrameReadyProjectId === projectId
}

export function markTimelineFirstFrameReady(projectId: string | null): void {
  if (!projectId || firstFrameReadyProjectId === projectId) return
  firstFrameReadyProjectId = projectId
  firstFrameListeners.forEach(listener => listener())
}

export function subscribeTimelineFirstFrameReady(listener: () => void): () => void {
  firstFrameListeners.add(listener)
  return () => {
    firstFrameListeners.delete(listener)
  }
}

// Monitors report while clip loads are in flight so the thumbnail fallback timer
// (TimelineClip) can defer to it instead of racing it to mark first-frame-ready.
// A count, not a flag: overlapping monitors (in-app project switch, docked + expanded) each
// own their own contribution, so one unmounting can't clear another's in-flight load.
let timelineMonitorLoads = 0

export function beginTimelineMonitorLoad(): () => void {
  timelineMonitorLoads++
  let ended = false
  return () => {
    if (ended) return
    ended = true
    timelineMonitorLoads--
  }
}

export function isTimelineMonitorLoading(): boolean {
  return timelineMonitorLoads > 0
}

export function setTimelinePlaybackActive(active: boolean): void {
  if (timelinePlaybackActive === active) return

  timelinePlaybackActive = active
  if (active) {
    cancelDeferredFlush()
  } else {
    scheduleDeferredBackgroundWorkFlush()
  }

  listeners.forEach(listener => listener(active))
}

export function subscribeTimelinePlaybackActivity(listener: PlaybackActivityListener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function schedulePlaybackAwareBackgroundWork(key: string, kind: string, run: () => void): () => void {
  let cancelled = false
  const wrapped = () => {
    if (cancelled) return
    run()
  }

  if (!timelinePlaybackActive) {
    wrapped()
    return () => {
      cancelled = true
    }
  }

  const existing = deferredWork.get(key)
  if (existing) {
    existing.run = wrapped
    existing.queuedAt = now()
    recordPlaybackBackgroundWork(kind, 'skipped', { reason: 'deduped-deferred-work' })
  } else {
    deferredWork.set(key, { key, kind, run: wrapped, queuedAt: now() })
    recordPlaybackBackgroundWork(kind, 'deferred', { reason: 'timeline-playback-active' })
  }

  return () => {
    cancelled = true
    const pending = deferredWork.get(key)
    if (pending?.run === wrapped) {
      deferredWork.delete(key)
      recordPlaybackBackgroundWork(kind, 'skipped', { reason: 'canceled-deferred-work' })
    }
  }
}

export function getPlaybackBackgroundWorkSnapshot(): {
  isTimelinePlaybackActive: boolean
  queued: number
  actions: Record<string, number>
  byKind: Record<string, Record<string, number>>
} {
  return {
    isTimelinePlaybackActive: timelinePlaybackActive,
    queued: deferredWork.size,
    actions: { ...actionCounts },
    byKind: Object.fromEntries(Object.entries(kindActionCounts).map(([kind, counts]) => [kind, { ...counts }])),
  }
}

export function __resetPlaybackActivityForTests(): void {
  cancelDeferredFlush()
  timelinePlaybackActive = false
  deferredWork.clear()
  listeners.clear()
  for (const key of Object.keys(actionCounts)) delete actionCounts[key]
  for (const key of Object.keys(kindActionCounts)) delete kindActionCounts[key]
  firstFrameReadyProjectId = null
  timelineMonitorLoads = 0
}
